import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  getDoc,
  collectionData,
  docData,
  where,
  orderBy,
  limit,
  query,
} from '@angular/fire/firestore';
import { firstValueFrom, Observable } from 'rxjs';
import * as moment from 'moment';
import {
  addDays,
  differenceInDays,
  format,
  isAfter,
  isBefore,
  isSameDay,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns';
import * as _ from 'lodash';
import { UserService } from './user.service';
import { PeriodEvent } from '../models/event';
import { Cycle, CycleStatus, Period, Pregnancy } from '../models/period';

@Injectable({
  providedIn: 'root',
})
export class DiaryService {
  private userDiaryPathV0: any;
  private userDiaryPathV1: any;
  currentPeriodPhaseString: string = '';

  constructor(private fs: Firestore, private userService: UserService) {
    this.userDiaryPathV0 = doc(this.fs, `v0/data/user/${this.userService.user.id}`);
    this.userDiaryPathV1 = doc(this.fs, `v1/data/user/${this.userService.user.id}`);
  }

  getUserDiary(): Observable<any[]> {
    const eventsRef = collection(this.userDiaryPathV1, 'events');
    return collectionData(eventsRef);
  }

  async getUserDiaryOnce(): Promise<any[]> {
    const eventsRef = collection(this.userDiaryPathV1, 'events');
    return firstValueFrom(collectionData(eventsRef));
  }

  async getUserPeriods(): Promise<Period[]> {
    const periodsRef = collection(this.userDiaryPathV0, 'periods');
    const snapshot = await getDocs(periodsRef);
    return snapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as Omit<Period, 'id'>) }))
      .sort((a, b) => +new Date(a.startedOn) - +new Date(b.startedOn));
  }

  buildCycles(periods: Period[], cycleLength: number = 28): Cycle[] {
    if (!periods || periods.length === 0) return [];

    const cycles = periods.map((current, i) => {
      const next = periods[i + 1];

      const startedOn = current.startedOn;
      let endedOn: string | null;
      let status: CycleStatus;

      if (next) {
        endedOn = moment(next.startedOn).subtract(1, "day").format("YYYY-MM-DD");
        status = "past";
      } else {
        endedOn = null;
        status = "current";
      }

      return new Cycle(startedOn, status, endedOn);
    });


    let nextStart = moment(periods[periods.length - 1].startedOn, "YYYY-MM-DD", true);
    for (let i = 0; i < 3; i++) {
      nextStart = nextStart.add(cycleLength, "day");
      cycles.push(new Cycle(nextStart.format("YYYY-MM-DD"), "predicted", null));
    }

    return cycles;
  }



  async getUserPregnancies(): Promise<Pregnancy[]> {
    const pregRef = collection(this.userDiaryPathV0, 'pregnancies');
    const snapshot = await getDocs(pregRef);
    return snapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as Omit<Pregnancy, 'id'>) }))
      .sort((a, b) => +new Date(a.startDate) - +new Date(b.startDate));
  }


  setCurrentPeriodPhase(value: string): void {
    this.currentPeriodPhaseString = value;
  }

  getCurrentPeriodPhase(): string {
    return this.currentPeriodPhaseString;
  }

  saveUserMoods(date: string, moods: number[]): Promise<void> {
    const eventDoc = doc(this.userDiaryPathV1, `events/${date}`);
    return setDoc(eventDoc, { moods }, { merge: true });
  }

  saveUserSymptoms(date: string, symptoms: number[]) {
    const uid = this.userService.user.id;

    return Promise.all([
      setDoc(
        doc(
          collection(
            this.fs,
            'v1', 'data', 'user', uid, 'events'
          ),
          date
        ),
        { symptoms: symptoms },
        { merge: true }
      ),
      Promise.resolve(symptoms),
    ]);
  }

  async getLastOpenPeriod(): Promise<Period | null> {
    const periodsRef = collection(this.userDiaryPathV0, 'periods');
    const q = query(periodsRef, orderBy('startedOn', 'desc'), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...(d.data() as Omit<Period, 'id'>) };
  }

  saveUserNote(date: string, data: any): Promise<void> {
    const eventDoc = doc(this.userDiaryPathV1, `events/${date}`);
    return setDoc(eventDoc, { ...data }, { merge: true });
  }

  async savePeriodEvent(date: string, event: PeriodEvent): Promise<void> {
    const eventDoc = doc(this.userDiaryPathV1, `events/${date}`);
    const docSnap = await getDoc(eventDoc);

    if (docSnap.exists()) {
      await updateDoc(eventDoc, { ...event });
    } else {
      await setDoc(eventDoc, { ...event });
    }
  }

  getUserSymptoms(date: string): Observable<any> {
    const docRef = doc(this.userDiaryPathV1, `events/${date}`);
    return docData(docRef);
  }

  insertUserPeriod(start: string): Promise<any> {
    const periodsRef = collection(this.userDiaryPathV0, 'periods');
    return addDoc(periodsRef, { startedOn: start, endedOn: null, cycleLength: null });
  }

  updateUserPeriod(docId: string, end: string): Promise<void> {
    const periodDoc = doc(this.userDiaryPathV0, `periods/${docId}`);
    return updateDoc(periodDoc, { endedOn: end });
  }

  deletePeriod(docId: string): Promise<void> {
    const periodDoc = doc(this.userDiaryPathV0, `periods/${docId}`);
    return deleteDoc(periodDoc);
  }

  isEmpty(event: any): boolean {
    const keys = Object.keys(event);
    for (const key of keys) {
      const value = this[key];
      if (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        return true;
      }
    }
    return false;
  }

  getPeriodPhase(item: Period, periodDays: number = null, nextPeriodInDays: number = null): any {
    if (!item) {
      return { state: 'No data', day: null };
    }

    if (item.endedOn == null) {
      return 'Period';
    }

    let late;
    const today: moment.Moment = moment();
    const nextPeriodStart = format(
      addDays(new Date(item.startedOn), nextPeriodInDays ?? 28),
      'yyyy-MM-dd'
    );
    const daysFromLastPeriod =
      differenceInDays(
        parseISO(moment().format('YYYY-MM-DD')),
        parseISO(item.startedOn)
      ) - 1;
    const ovulation = moment(nextPeriodStart)
      .subtract(14, 'days')
      .format('YYYY-MM-DD');

    if (daysFromLastPeriod > 28) {
      late = {
        start: parseISO(nextPeriodStart),
        end: new Date(addDays(parseISO(nextPeriodStart), daysFromLastPeriod)),
      };
    } else {
      late = {};
    }

    const periodObj = {
      ovulation: { start: ovulation },
      period: {
        start: parseISO(item.startedOn),
        end: item.endedOn
          ? new Date(parseISO(item.endedOn).setHours(23, 59, 59, 999))
          : new Date(),
      },
      follicular: {
        start: new Date(addDays(parseISO(item.endedOn), 1).setHours(0, 0, 0, 0)),
        end: new Date(subDays(parseISO(ovulation), 6).setHours(23, 59, 59, 999)),
      },
      fertile: {
        start: subDays(parseISO(ovulation), 5),
        end: new Date(addDays(parseISO(ovulation), 1).setHours(23, 59, 59, 999)),
      },
      luteal: {
        start: new Date(addDays(parseISO(ovulation), 2).setHours(0, 0, 0, 0)),
        end: new Date(parseISO(nextPeriodStart).setHours(23, 59, 59, 999)),
      },
      nextPeriodStart: parseISO(nextPeriodStart),
      late: late,
      cycleLength: parseISO(item.startedOn),
    };

    let state = '';
    const dateEnd = new Date();

    if (!_.isEmpty(periodObj.fertile)) {
      if (today.isBetween(periodObj.fertile.start, periodObj.fertile.end, 'day', '[]')) {
        state = 'Fertile';
      }
    }

    if (!_.isEmpty(periodObj.late)) {
      if (today.isBetween(periodObj.late.start, periodObj.late.end, 'day', '[]')) {
        state = 'Late';
      }
    }

    if (periodObj.ovulation) {
      if (isSameDay(dateEnd, new Date(periodObj.ovulation.start))) {
        state = 'Ovulation';
      }
    }

    if (!_.isEmpty(periodObj.luteal)) {
      if (today.isBetween(periodObj.luteal.start, periodObj.luteal.end, 'day', '[]')) {
        state = 'Luteal';
      }
    }

    if (!_.isEmpty(periodObj.follicular)) {
      if (
        today.isBetween(periodObj.follicular.start, periodObj.follicular.end, 'day', '[]')
      ) {
        state = 'Follicular';
      }
    }

    if (!_.isEmpty(periodObj.period)) {
      if (today.isBetween(periodObj.period.start, periodObj.period.end, 'day', '[]')) {
        state = 'Period';
      }
    }

    const daysTillToday = differenceInDays(new Date(), parseISO(item.startedOn));
    if (periodObj && daysTillToday > 28 && isAfter(new Date(), periodObj.period.start)) {
      state = 'Late';
    }

    if (!item.endedOn) {
      const periodEnd = addDays(
        new Date(item.startedOn),
        periodDays != null ? periodDays : 5
      );
      if (isAfter(new Date(), periodEnd)) {
        state = 'Late';
      }
    }

    return state;
  }

  getCycleDay(item: Period, cycleLengthPredicted: number): number | null {
    if (!item?.startedOn || !cycleLengthPredicted) return null;

    const start = startOfDay(typeof item.startedOn === 'string'
      ? parseISO(item.startedOn)
      : new Date(item.startedOn));

    const nextStart = startOfDay(addDays(start, cycleLengthPredicted));
    const today = startOfDay(new Date());

    if (isBefore(today, start) || !isBefore(today, nextStart)) return null;

    return differenceInDays(today, start) + 1;
  }

  getSymptoms(): any[] {
    return [
      {
        title: 'Flow',
        objectTitle: 'flow',
        multipleSelection: false,
        items: [
          { id: 1, label: 'Light', value: 'light', icon: 'moods/ic_flow_light.svg' },
          { id: 2, label: 'Medium', value: 'medium', icon: 'moods/ic_flow_medium.svg' },
          { id: 3, label: 'Heavy', value: 'heavy', icon: 'moods/ic_flow_heavy.svg' },
        ],
      },
      {
        title: 'Sex',
        objectTitle: 'sex',
        multipleSelection: true,
        items: [
          { id: 1, label: 'Protected Sex', value: 'protectedSex', icon: 'moods/ic_sex_protected.svg' },
          { id: 2, label: 'Unprotected sex', value: 'unprotectedSex', icon: 'moods/ic_sex_unprotected.svg' },
          { id: 3, label: 'Masturbation', value: 'masturbation', icon: 'moods/ic_sex_masturbation.svg' },
          { id: 4, label: 'High sex drive', value: 'highSexDrive', icon: 'moods/ic_sex_high_sex_drive.svg' },
          { id: 5, label: 'Feeling sexy', value: 'feelingSexy', icon: 'moods/ic_sex_sexy.svg' },
          { id: 6, label: 'Low libido', value: 'lowLibido', icon: 'moods/ic_sex_low_libido.svg' },
        ],
      },
      {
        title: 'Discharge',
        objectTitle: 'discharge',
        multipleSelection: false,
        items: [
          { id: 1, label: 'Spotting', value: 'spotting', icon: 'moods/ic_discharge_spotting.svg' },
          { id: 2, label: 'Sticky', value: 'sticky', icon: 'moods/ic_discharge_sticky.svg' },
          { id: 3, label: 'Creamy', value: 'creamy', icon: 'moods/ic_discharge_creamy.svg' },
          { id: 4, label: 'Egg White', value: 'eggWhite', icon: 'moods/ic_discharge_eggwhite.svg' },
          { id: 5, label: 'Watery', value: 'watery', icon: 'moods/ic_discharge_watery.svg' },
          { id: 6, label: 'Unusual', value: 'unusual', icon: 'moods/ic_discharge_unusual.svg' },
        ],
      },
      {
        title: 'Moods',
        objectTitle: 'moods',
        multipleSelection: true,
        items: [
          { id: 1, label: 'Angry', value: 'angry', icon: 'moods/ic_moods_angry.svg' },
          { id: 2, label: 'Calm', value: 'calm', icon: 'moods/ic_moods_calm.svg' },
          { id: 3, label: 'Confused', value: 'confused', icon: 'moods/ic_moods_confused.svg' },
          { id: 4, label: 'Depressed', value: 'depressed', icon: 'moods/ic_moods_depressed.svg' },
          { id: 5, label: 'Energetic', value: 'energetic', icon: 'moods/ic_moods_energetic.svg' },
          { id: 8, label: 'Happy', value: 'happy', icon: 'moods/ic_moods_happy.svg' },
          { id: 12, label: 'Lethargic', value: 'lethargic', icon: 'moods/ic_moods_lethargic.svg' },
          { id: 14, label: 'Moody', value: 'moody', icon: 'moods/ic_moods_moody.svg' },
          { id: 16, label: 'Sad', value: 'sad', icon: 'moods/ic_moods_sad.svg' },
          { id: 23, label: 'Anxious', value: 'anxious', icon: 'moods/ic_moods_anxious.svg' },
          { id: 24, label: 'Feeling Guilty', value: 'guilty', icon: 'moods/ic_moods_guilty.svg' },
          { id: 25, label: 'Irritated', value: 'irritated', icon: 'moods/ic_moods_irritated.svg' },
          { id: 26, label: 'Obsessive', value: 'obsessive', icon: 'moods/ic_moods_obsessive_thoughts.svg' },
          { id: 27, label: 'Self-Critical', value: 'selfCritical', icon: 'moods/ic_moods_self_critical.svg' },
          { id: 28, label: 'Confident', value: 'confident', icon: 'moods/ic_moods_confident.svg' },
          { id: 29, label: 'Frisky', value: 'frisky', icon: 'moods/ic_moods_frisky.svg' },
          { id: 30, label: 'Apathetic', value: 'apathetic', icon: 'moods/ic_moods_apathetic.svg' },
          { id: 31, label: 'Overwhelmed', value: 'overwhelmed', icon: 'moods/ic_moods_overwhelmed.svg' },
        ],
      },
      {
        title: 'Symptoms',
        objectTitle: 'symptoms',
        multipleSelection: true,
        items: [
          { id: 1, label: 'Acne', value: 'acne', icon: 'moods/ic_symptoms_acne.svg' },
          { id: 3, label: 'Backache', value: 'backache', icon: 'moods/ic_symptoms_backache.svg' },
          { id: 4, label: 'Constipation', value: 'constipation', icon: 'moods/ic_symptoms_constipation.svg' },
          { id: 5, label: 'Cramps', value: 'cramps', icon: 'moods/ic_symptoms_cramps.svg' },
          { id: 6, label: 'Cravings', value: 'cravings', icon: 'moods/ic_symptoms_cravings.svg' },
          { id: 9, label: 'Diarrhea', value: 'diarrhea', icon: 'moods/ic_symptoms_diarrhea.svg' },
          { id: 10, label: 'Dizziness', value: 'dizziness', icon: 'moods/ic_symptoms_dizziness.svg' },
          { id: 12, label: 'Headache', value: 'headache', icon: 'moods/ic_symptoms_headache.svg' },
          { id: 13, label: 'Insomnia', value: 'insomnia', icon: 'moods/ic_symptoms_insomnia.svg' },
          { id: 15, label: 'Bloating', value: 'bloating', icon: 'moods/ic_symptoms_bloating.svg' },
          { id: 16, label: 'Tender Breasts', value: 'tenderBreasts', icon: 'moods/ic_symptoms_tender_breasts.svg' },
          { id: 17, label: 'Fatigue', value: 'fatigue', icon: 'moods/ic_symptoms_fatigue.svg' },
          { id: 20, label: 'Nausea', value: 'nausea', icon: 'moods/ic_symptoms_nausea.svg' },
          { id: 21, label: 'Sore Throat', value: 'soreThroat', icon: 'moods/ic_symptoms_sore_throat.svg' },
          { id: 22, label: 'Fever', value: 'fever', icon: 'moods/ic_symptoms_fever.svg' },
          { id: 23, label: 'Hot Flashes', value: 'hotFlashes', icon: 'moods/ic_symptoms_hot_flashes.svg' },
          { id: 24, label: 'Joint Pain', value: 'jointPain', icon: 'moods/ic_symptoms_joint_pain.svg' },
          { id: 24, label: 'Swelling', value: 'swelling', icon: 'moods/ic_symptoms_swelling.svg' },
          { id: 26, label: 'Eczema', value: 'eczema', icon: 'moods/ic_symptoms_eczema.svg' },
          { id: 27, label: 'Clumsiness', value: 'clumsiness', icon: 'moods/ic_symptoms_clumsiness.svg' },
          { id: 28, label: 'Light Sensitivity', value: 'lightSensitivity', icon: 'moods/ic_symptoms_light_sensitivity.svg' },
          { id: 29, label: 'Lack of Appetite', value: 'lackOfAppetite', icon: 'moods/ic_symptoms_lack_of_appetite.svg' },
          { id: 30, label: 'Trouble Concentrating', value: 'troubleConcentrating', icon: 'moods/ic_symptoms_trouble_concentrating.svg' },
          { id: 31, label: 'Crying Spells', value: 'cryingSpells', icon: 'moods/ic_symptoms_crying_spells.svg' },
          { id: 32, label: 'Hypersomnia', value: 'hypersomnia', icon: 'moods/ic_symptoms_hypersomnia.svg' },
          { id: 33, label: 'Lack of Energy', value: 'lackOfEnergy', icon: 'moods/ic_symptoms_lack_of_energy.svg' },
          { id: 34, label: 'Loss of Interest', value: 'lossOfInterest', icon: 'moods/ic_symptoms_loss_of_interest.svg' },
        ],
      },
      {
        title: 'Contraception',
        objectTitle: 'contraception',
        multipleSelection: true,
        items: [
          { id: 1, label: 'Pill', value: 'pill', icon: 'moods/ic_contraception_pill.svg' },
          { id: 2, label: 'Forgot Pill', value: 'forgotPill', icon: 'moods/ic_contraception_forgotten_pill.svg' },
          { id: 3, label: "Yesterday's Pill", value: 'yesterdaysPill', icon: 'moods/ic_contraception_yesterday_pill.svg' },
          { id: 4, label: 'Plan B', value: 'planB', icon: 'moods/ic_contraception_plan_b.svg' },
          { id: 5, label: 'Broken condom', value: 'brokenCondom', icon: 'moods/ic_contraception_broken_condom.svg' },
          { id: 6, label: 'Pull-out Method', value: 'pullOut', icon: 'moods/ic_contraception_pull_out.svg' },
          { id: 7, label: 'Contraceptive Ring', value: 'contraceptiveRing', icon: 'moods/ic_contraception_ring.svg' },
          { id: 8, label: 'Contraceptive Patch', value: 'contraceptivePatch', icon: 'moods/ic_contraception_patch.svg' },
          { id: 9, label: 'Condom', value: 'condom', icon: 'moods/ic_contraception_condom.svg' },
        ],
      },
      {
        title: 'Medication',
        objectTitle: 'medication',
        multipleSelection: true,
        items: [
          { id: 1, label: 'Pain Reliever', value: 'painReliever', icon: 'moods/ic_medication_pain_relief.svg' },
          { id: 2, label: 'Allergy Reliever', value: 'allergyReliever', icon: 'moods/ic_medication_allergy_reliever.svg' },
          { id: 3, label: 'My Medication', value: 'myMedication', icon: 'moods/ic_medication_my_medication.svg' },
        ],
      },
      {
        title: 'Supplements',
        objectTitle: 'supplements',
        multipleSelection: true,
        items: [
          { id: 1, label: 'Calcium', value: 'calcium', icon: 'moods/ic_supplements_calcium.svg' },
          { id: 2, label: 'Iron', value: 'iron', icon: 'moods/ic_supplements_iron.svg' },
          { id: 3, label: 'Magnesium', value: 'magnesium', icon: 'moods/ic_supplements_magnesium.svg' },
          { id: 4, label: 'Niacin', value: 'niacin', icon: 'moods/ic_supplements_niacin.svg' },
          { id: 5, label: 'Selenium', value: 'selenium', icon: 'moods/ic_supplements_selenium.svg' },
          { id: 6, label: 'Vitamin A', value: 'vitaminA', icon: 'moods/ic_supplements_vitamin_a.svg' },
          { id: 7, label: 'Vitamin B6', value: 'vitaminB6', icon: 'moods/ic_supplements_vitamin_b6.svg' },
          { id: 8, label: 'Vitamin B12', value: 'vitaminB12', icon: 'moods/ic_supplements_vitamin_b12.svg' },
          { id: 9, label: 'Vitamin C', value: 'vitaminC', icon: 'moods/ic_supplements_vitamin_c.svg' },
          { id: 10, label: 'Vitamin D', value: 'vitaminD', icon: 'moods/ic_supplements_vitamin_d.svg' },
          { id: 11, label: 'Zinc', value: 'zinc', icon: 'moods/ic_supplements_zinc.svg' },
          { id: 12, label: 'Collagen', value: 'collagen', icon: 'moods/ic_supplements_collagen.svg' },
          { id: 13, label: 'Whey Protein', value: 'wheyProtein', icon: 'moods/ic_supplements_whey_protein.svg' },
          { id: 14, label: 'Ashwagandha', value: 'ashwagandha', icon: 'moods/ic_supplements_ashwagandha.svg' },
          { id: 15, label: 'CBD', value: 'cbd', icon: 'moods/ic_supplements_cbd.svg' },
          { id: 16, label: 'Ginger', value: 'ginger', icon: 'moods/ic_supplements_ginger.svg' },
          { id: 17, label: 'Ginseng', value: 'ginseng', icon: 'moods/ic_supplements_ginseg.svg' },
          { id: 18, label: 'Maca', value: 'maca', icon: 'moods/ic_supplements_maca.svg' },
          { id: 19, label: 'Turmeric', value: 'turmeric', icon: 'moods/ic_supplements_tumeric.svg' },
          { id: 20, label: 'Valerian Root', value: 'valerianRoot', icon: 'moods/ic_supplements_valerian_root.svg' },
          { id: 21, label: 'Folic Acid', value: 'folicAcid', icon: 'moods/ic_supplements_valerian_root.svg' },
        ],
      },
      {
        title: 'Diet',
        objectTitle: 'diet',
        multipleSelection: true,
        items: [
          { id: 1, label: 'Junk Food', value: 'junkFood', icon: 'moods/ic_diet_junk_food.svg', healthy: false },
          { id: 2, label: 'Cigarettes', value: 'cigarettes', icon: 'moods/ic_diet_cigarettes.svg', healthy: false },
          { id: 3, label: 'Alcohol', value: 'alcohol', icon: 'moods/ic_diet_alcohol.svg', healthy: false },
          { id: 4, label: 'Sugar', value: 'sugar', icon: 'moods/ic_diet_sugar.svg', healthy: false },
          { id: 5, label: 'Salty snack', value: 'saltySnack', icon: 'moods/ic_diet_salty_snack.svg', healthy: false },
          { id: 6, label: 'Injury', value: 'injury', icon: 'moods/ic_diet_injury.svg', healthy: false },
          { id: 7, label: 'Stressed', value: 'stressed', icon: 'moods/ic_diet_stressed.svg', healthy: false },
          { id: 8, label: 'Socialising', value: 'socializing', icon: 'moods/ic_diet_socialising.svg', healthy: true },
          { id: 9, label: 'Massage', value: 'massage', icon: 'moods/ic_diet_massage.svg', healthy: true },
          { id: 10, label: 'Acupuncture', value: 'acupuncture', icon: 'moods/ic_diet_acupuncture.svg', healthy: true },
          { id: 11, label: 'Spa', value: 'spa', icon: 'moods/ic_diet_spa.svg', healthy: true },
          { id: 12, label: 'Hobby', value: 'hobby', icon: 'moods/ic_diet_hobby.svg', healthy: true },
          { id: 13, label: 'Travel', value: 'travel', icon: 'moods/ic_diet_travel.svg', healthy: true },
          { id: 14, label: 'Self-Care Day', value: 'selfCare', icon: 'moods/ic_diet_self_care.svg', healthy: true },
        ],
      },
    ];
  }

  removeItemsByKey(array: any[], key: string, valueToRemove: string): any[] {
    return array.filter((item) => item[key] !== valueToRemove);
  }
}
